// PWA Service Worker Hook
// Integrates with vite-plugin-pwa for manual updates and PWA functionality

import { useCallback, useEffect, useState } from 'react';
import { useToastr } from '@farmshare/ui-components';
import { Refresh } from '@mui/icons-material';

interface PWAUpdateProps {
  onNeedRefresh?: () => void;
  onOfflineReady?: () => void;
}

export function usePWA({ onNeedRefresh, onOfflineReady }: PWAUpdateProps = {}) {
  const [needRefresh, setNeedRefresh] = useState(false);
  // const [offlineReady, setOfflineReady] = useState(false);
  const { push } = useToastr();
  const [hasShownUpdateToast, setHasShownUpdateToast] = useState(false);

  const updateApp = useCallback(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistration().then(async (registration) => {
        if (registration?.waiting) {
          // Tell the waiting service worker to skip waiting and become active
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        }

        // Force update and refresh all service worker registrations
        if (registration) {
          await registration.update();
        }

        // Clear all caches to ensure fresh content
        if ('caches' in window) {
          const cacheNames = await caches.keys();
          await Promise.all(
            cacheNames.map((cacheName) => caches.delete(cacheName)),
          );
        }
      });
    }

    // Add a small delay to allow cache clearing to complete
    setTimeout(() => {
      window.location.reload();
    }, 100);
  }, []);

  useEffect(() => {
    // Check if PWA plugin has registered update functions
    if (!('serviceWorker' in navigator)) {
      return;
    }

    let updateInterval: NodeJS.Timeout;
    let checkForUpdates: (() => void) | undefined;

    // Listen for PWA update events
    const handleUpdateAvailable = () => {
      setNeedRefresh(true);
      onNeedRefresh?.();

      if (hasShownUpdateToast) return;
      setHasShownUpdateToast(true);

      // Show toast notification for update
      push({
        id: 'pwa-update',
        severity: 'info',
        title: 'An Update is Available',
        body: 'A new version of the app is available. Please refresh to see the latest changes.',
        showConfetti: true,
        delay: 20000,
        action: {
          label: 'Refresh',
          onClick: updateApp,
          startIcon: <Refresh />,
        },
      });
    };

    // Listen for controller change (new SW activated)
    const handleControllerChange = () => {
      if (needRefresh) {
        window.location.reload();
      }
    };

    // Check for existing vite-pwa registration
    navigator.serviceWorker.getRegistration().then((registration) => {
      if (registration) {
        // Listen for updates
        registration.addEventListener('updatefound', handleUpdateAvailable);

        // If service worker is waiting (update ready), handle it
        if (registration.waiting) {
          handleUpdateAvailable();
        }

        // Check for updates periodically (every 30 seconds when page is active)
        checkForUpdates = () => {
          if (document.visibilityState === 'visible') {
            registration.update().catch(() => {
              // Silently fail if update check fails
            });
          }
        };

        // Set up periodic update checks
        updateInterval = setInterval(checkForUpdates, 30000);
        document.addEventListener('visibilitychange', checkForUpdates);
      }
    });

    navigator.serviceWorker.addEventListener(
      'controllerchange',
      handleControllerChange,
    );

    // Cleanup function
    // eslint-disable-next-line consistent-return
    return () => {
      if (updateInterval) {
        clearInterval(updateInterval);
      }
      if (checkForUpdates) {
        document.removeEventListener('visibilitychange', checkForUpdates);
      }
      navigator.serviceWorker.removeEventListener(
        'controllerchange',
        handleControllerChange,
      );
    };
  }, [
    needRefresh,
    onNeedRefresh,
    onOfflineReady,
    push,
    hasShownUpdateToast,
    updateApp,
  ]);

  return {
    needRefresh,
    // offlineReady,
    updateApp,
  };
}
